// fetch-based SSE client for the PLG demo widget.
// Lightweight equivalent of the webapp's lib/sse.ts — no auth header
// (demo uses session_token in body), no AbortController complexity.

export async function streamDemoMessage({
  base, sessionToken, query, handlers, signal,
}) {
  const url = `${base.replace(/\/$/, '')}/api/demo/message`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
    body: JSON.stringify({ session_token: sessionToken, user_query: query }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    let err;
    try { err = await resp.json(); } catch { err = {}; }
    handlers.onError?.({
      status:  resp.status,
      message: err?.detail || friendlyError(resp.status),
    });
    return;
  }
  const reader  = resp.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buf = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    buf = buf.replace(/\r/g, '');
    let idx;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
      dispatch(frame, handlers);
    }
  }
  if (buf.trim()) dispatch(buf, handlers);
}

function dispatch(frame, h) {
  let event = 'message';
  let dataLines = [];
  for (const raw of frame.split('\n')) {
    const line = raw.replace(/\r$/, '');
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return;
  let data;
  try { 
    data = JSON.parse(dataLines.join('\n')); 
    if (typeof data === 'string') data = { text: data };
  } catch { 
    data = { text: dataLines.join('\n') }; 
  }
  switch (event) {
    case 'phase':           return h.onPhase?.(data);
    case 'plan':            return h.onPlan?.(data);
    case 'tool_start':      return h.onToolStart?.(data);
    case 'tool_end':        return h.onToolEnd?.(data);
    case 'message':         // Fallback if backend does not explicitly set `event: token`
    case 'token':           return h.onToken?.(data);
    case 'thinking':        return h.onThinking?.(data);
    case 'code':            return h.onCode?.(data);
    case 'code_out':        return h.onCodeOut?.(data);
    case 'widget_candidate':return h.onWidgetCandidate?.(data);
    case 'feedback_prompt': return h.onFeedbackPrompt?.(data);
    case 'done':            return h.onDone?.(data);
    case 'error':           return h.onError?.(data);
    default: /* ignore */
  }
}

export function friendlyError(status) {
  switch (status) {
    case 401: return 'Your demo session expired. Please verify your phone again.';
    case 402: return 'You have hit the demo budget (₹250). Activate Axia to continue.';
    case 410: return 'This demo session is over.';
    case 413: return 'That file is too large. The demo accepts files up to 100 MB.';
    case 422: return 'Could not understand the request. Please try again.';
    case 429: return 'Too many requests. Wait a minute and try again.';
    default:  return 'Something went wrong. Please try again.';
  }
}
